import React, { useEffect, useState } from "react";
import resumeJSON from "../assets/json/resume.json";
import { customParser } from "../utils/helpers";
import "./Resume2.scss";

function Resume() {

  const [resumeStyle, setResumeStyle] = useState()

  useEffect(() => {
    setResumeStyle({
      opacity: "1",
      // fontSize: `${windowRatio}%`
    })
  }, []);

  return (
    <section
      id="Resume"
      style={resumeStyle}
    >
      <h1 id="banner">RESUME</h1>

      {/* <div className="intro">
      </div> */}

      <div className="resume-container">
        
        {/* MAIN CONTENT */}
        <main className="resume-main-content">
          
          {/* SUMMARY */}
          <section className="resume-section">
            <h2 className="resume-header">Engineering Focus.</h2>
            <p className="summary-text">
              I am a software engineer focused on architecting and delivering end-to-end .NET web, mobile, and real-time systems. I specialize in translating complex business requirements into scalable, event-driven architecture, tackling technical challenges like <strong>real-time concurrency, high-throughput mobile data syncing, and multi-tenant Role-Based Access Control (RBAC)</strong>. Beyond writing code, I actively support the team's delivery process by maintaining clean version control hygiene, untangling stale backlogs, and helping establish predictable, sustainable release cycles.
            </p>
          </section>

          {/* SKILLS ASIDE -> inline as section here */}
          <section className="resume-sidebar">
            <h2 className="resume-header">Core Competencies</h2>
            <div className="skills-grid">
              <div className="skills-group">
                <h3>Backend & Data</h3>
                <p>C#, .NET 7/8, ASP.NET Core Web API, SignalR, EF Core, Dapper, SQL Server</p>
              </div>
              <div className="skills-group">
                <h3>Architecture & Logic</h3>
                <p>Multithreading, Task-Based Concurrency, Async Patterns, Custom Auth, RBAC & Permissions, Schema Design, Observability</p>
              </div>
              <div className="skills-group">
                <h3>Mobile & Frontend</h3>
                <p>.NET MAUI, Background Tasks/Workers, Firebase Cloud Messaging, APNs, JavaScript, Web Workers, jQuery/Kendo UI</p>
              </div>
              <div className="skills-group">
                <h3>Delivery & Ops</h3>
                <p>Release Management, Azure, IIS, DB Admin (Backups/Restores), PowerShell, Twilio REST API, ColdFusion, Docker</p>
              </div>
            </div>
          </section>

          {/* EXPERIENCE */}
          <section className="resume-section">
            <h2 className="resume-header">Experience</h2>
            
            <div className="job">
              <div className="job-header">
                <div>
                  <span className="job-title">Full-Stack Software Engineer / Release Engineer</span>
                </div>
                <span className="job-date">03/2023 – PRESENT</span>
              </div>
              <div className="job-company">Amerinational Management Services (AMS) • Orlando, FL</div>
              
              <ul className="impact-list">
                <li><strong>Architected a real-time ecosystem:</strong> Built a SignalR hub (scaled via Azure SignalR Service) powering a live chat dashboard, group rooms, and high-volume broadcast/mass messaging.</li>
                <li><strong>Owned API architecture & multi-tenant RBAC:</strong> Designed ASP.NET Core REST endpoints and optimized Entity Framework queries for efficient SQL Server data access. Retrofitted complex multi-tenant Role-Based Access Control (RBAC) to enforce application access and visibility rules.</li>
                <li><strong>Managed mobile concurrency & state:</strong> Synchronized 200+ accounts (images/chat logs) on .NET MAUI clients without blocking the UI thread. Implemented bounded concurrency (max 10 concurrent tasks) to prevent resource exhaustion, and datetime-keyed hashing for fast lookups during heavy data syncs.</li>
                <li><strong>Implemented full-stack observability:</strong> Added correlation IDs for end-to-end tracing across API and device logs. Engineered client-side resilience with exponential backoff retries to handle network drops.</li>
                <li><strong>Streamlined identity & native push:</strong> Implemented custom email-based auth flows to reduce registration friction. Engineered a cross-platform push notification pipeline using Firebase Cloud Messaging (Android) and a custom APNs/JWT integration for iOS.</li>
                <li><strong>Automated ops & vendor tooling:</strong> Built admin dashboards (JavaScript, Kendo UI, ColdFusion) and a suite of PowerShell scripts to manage Twilio subaccounts, track verification statuses, and execute bulk number cancellations. Implemented AJAX polling to process long-running API tasks over a single-threaded runtime.</li>
                <li><strong>Audited messaging spend:</strong> Analyzed messaging logs to identify high volumes of inactive endpoints, surfacing data-driven recommendations to potentially blacklist dead numbers and reduce wasted SMS spend.</li>
                <li><strong>Drove release predictability:</strong> Coordinated QA testing, managed environment parity (DB backups, restores, and refreshes), and executed production pushes to reduce release risk.</li>
              </ul>
              
              <div className="highlight-box">
                <strong>Delivery & Velocity Highlight:</strong> Unblocked a long-stalled 80-ticket backlog through methodical branch management and QA validation. Established a sustainable ~2-week release cadence alongside hotfixes; shipped ~300 tickets to production in 6 months with minimal release-related incidents and no rollbacks.
              </div>
            </div>

            <div className="job">
              <div className="job-header">
                <div>
                  <span className="job-title">Full-Stack Developer</span>
                </div>
                <span className="job-date">06/2022 – 02/2023</span>
              </div>
              <div className="job-company">United Financial Resources • Bradenton, FL</div>
              
              <ul className="impact-list">
                <li><strong>Workflow Digitization:</strong> Delivered the ingestion + validation layer that made the transition to digital processing viable, improving data security and document handling.</li>
                <li><strong>Automated Parsing & Testing:</strong> Developed logic to parse highly inconsistent, human-entered application data. Built a unit testing suite to validate accuracy and catch edge cases across hundreds of real-world form variations.</li>
                <li><strong>API Orchestration & Schema Design:</strong> Built a consolidated API endpoint to perform business-rule analysis, writing normalized data into newly designed SQL Server relational tables to support downstream automation and reporting.</li>
                <li><strong>High-Throughput Systems:</strong> Maintained .NET APIs, internal NuGet dependencies, and complex SQL Server stored procedures powering high-throughput SMS campaigns and scheduling tools.</li>
                <li><strong>Frontend & Analytics:</strong> Maintained legacy React applications and implemented tracking integrations (Meta Pixel, Google Tag Manager) to improve marketing data quality and reporting.</li>
              </ul>
            </div>
          </section>

          {/* PROJECTS */}
          <section className="resume-section">
            <h2 className="resume-header">Projects</h2>
            <div className="job">
              <div className="job-title" style={{ marginBottom: "0.25rem" }}>Portfolio Architecture (<a href="https://jeff-timson.dev" style={{ color: "inherit", textDecoration: "underline" }}>jeff-timson.dev</a>)</div>
              <p style={{ fontSize: "0.95rem", fontStyle: "italic" }}>React/JavaScript, Firebase, Node.js</p>
            </div>
          </section>

          {/* EDUCATION */}
          <section className="resume-section">
            <h2 className="resume-header">Education & Certifications</h2>
            <ul className="impact-list" style={{ marginTop: 0 }}>
              <li><strong>University of Central Florida</strong> — Full Stack Web Development Certificate</li>
              <li><strong>Google</strong> — IT Support Professional Certificate</li>
              <li><strong>Valencia College</strong> — A.A. General Studies / Computer Science</li>
            </ul>
          </section>

        </main>
      </div>
    </section>
  )
}

export default Resume